/**
 * Map a free-text area/room name to a "room kind" so the UI can pick a
 * matching icon and pastel color. Pure — no framework dependencies.
 */

export type RoomKind =
  | 'bathroom'
  | 'kids'
  | 'bedroom'
  | 'kitchen'
  | 'dining'
  | 'laundry'
  | 'garage'
  | 'outdoor'
  | 'office'
  | 'living'
  | 'other';

/**
 * Ordered keyword rules. Order matters: the first rule with a matching keyword
 * wins, so more specific kinds come first (e.g. "Kids Bedroom" → kids,
 * "Garden" → outdoor before "den" → living, "Workshop" → garage before
 * "work" → office).
 */
const ROOM_RULES: ReadonlyArray<{ kind: RoomKind; keywords: readonly string[] }> = [
  { kind: 'bathroom', keywords: ['bath', 'toilet', 'restroom', 'shower', 'powder', 'washroom', 'ensuite', 'en-suite', 'wc', 'lavatory'] },
  { kind: 'kids', keywords: ['kid', 'child', 'nursery', 'playroom', 'play room', 'baby', 'toy'] },
  { kind: 'bedroom', keywords: ['bed', 'guest room', 'master', 'sleep'] },
  { kind: 'kitchen', keywords: ['kitchen', 'pantry', 'cook'] },
  { kind: 'dining', keywords: ['dining', 'breakfast'] },
  { kind: 'laundry', keywords: ['laundry', 'utility', 'linen'] },
  { kind: 'garage', keywords: ['garage', 'car', 'shed', 'workshop', 'basement', 'storage'] },
  { kind: 'outdoor', keywords: ['garden', 'yard', 'patio', 'balcony', 'deck', 'porch', 'outdoor', 'terrace', 'lawn', 'pool'] },
  { kind: 'office', keywords: ['office', 'study', 'desk', 'work', 'library'] },
  { kind: 'living', keywords: ['living', 'lounge', 'family', 'den', 'tv', 'sitting', 'hall', 'entry', 'foyer'] },
];

export const getRoomKind = (name: string | null | undefined): RoomKind => {
  const normalized = (name ?? '').trim().toLowerCase();
  if (!normalized) return 'other';
  for (const rule of ROOM_RULES) {
    if (rule.keywords.some((k) => normalized.includes(k))) {
      return rule.kind;
    }
  }
  return 'other';
};
